// Static pre-written translations for Clan Donald heritage site.
// Languages: English (en), French (fr), Spanish (es), German (de).

export const LANGUAGES = [
  { code: "en", label: "English", short: "EN", native: "English" },
  { code: "fr", label: "French", short: "FR", native: "Français" },
  { code: "es", label: "Spanish", short: "ES", native: "Español" },
  { code: "de", label: "German", short: "DE", native: "Deutsch" },
  { code: "it", label: "Italian", short: "IT", native: "Italiano" },
  { code: "gd", label: "Scottish Gaelic", short: "GD", native: "Gàidhlig" },
];

export const translations = {
  en: {
    nav: { history: "Origins", lineage: "Lineage", gallery: "Legacy", tartan: "The Tartan", join: "Join the Clan", houses: "Houses", kindred: "Kindred" },
    colors: { green: "Green", blue: "Blue", gold: "Gold" },
    brandTagline: "One Clan · Many Names",
    hero: {
      overline: "The McDonalds · Clann Dòmhnaill · Since the 12th Century",
      titleLines: ["By Sea", "and By", "Land"],
      subtitle:
        "The story of the McDonalds — not one man but a whole people. A many-branched clan whose blood runs through the glens of Scotland and the coasts of Ireland alike.",
      cta: "Discover Our Story",
      secondary: "Trace Your Lineage",
    },
    manifesto: {
      overline: "The Origins",
      title: "A kindred forged in salt, stone and fire.",
      chapters: [
        { num: "01", title: "Sons of Somerled", body: "From Somerled, the great Norse-Gaelic warlord who broke the grip of the sea-kings, our line descends — a people neither wholly Norse nor wholly Gael, but something fiercer than both." },
        { num: "02", title: "Lords of the Isles", body: "For three centuries the MacDonalds ruled as Lords of the Isles, a maritime kingdom of longships and law that answered to no crown but its own along the western seaboard." },
        { num: "03", title: "One Blood, Two Shores", body: "When the galleys crossed the North Channel, the MacDonnells took root in Antrim. To this day Ireland and Scotland share one heart, one name, one clan." },
      ],
    },
    timeline: {
      overline: "The Lineage",
      title: "Eight centuries, one unbroken thread.",
      events: [
        { year: "1156", title: "Somerled's Victory", body: "Somerled defeats the Norse fleet off Islay, laying the foundation of a Gaelic sea-kingdom." },
        { year: "1249", title: "Clann Dòmhnaill Named", body: "The descendants of Domhnall take his name, and the clan proper is born among the isles." },
        { year: "1411", title: "The Battle of Harlaw", body: "Donald of Islay marches on Aberdeen — the clan at the height of its martial power." },
        { year: "1493", title: "Fall of the Lordship", body: "The Lordship of the Isles is forfeited to the Scottish crown, scattering the clan into proud branches." },
        { year: "1565", title: "The Antrim Branch", body: "Sorley Boy MacDonnell secures the Glens of Antrim, binding Ireland forever to the MacDonald name." },
      ],
    },
    gallery: {
      overline: "The Legacy",
      title: "Land that remembers.",
      subtitle: "The glens, ruins and shores that carry our name.",
      captions: ["Highland strongholds", "The western coast", "Ancestral ruins", "Isle of the ancestors", "The old bridge"],
    },
    tartan: {
      overline: "The Tartan",
      title: "Threads of allegiance.",
      body: "Every colour is a vow. The deep red of courage, the green of the glens, the dark of the northern sea — woven into a pattern worn with honour from Skye to the Antrim coast.",
      motto: "Per mare per terras",
      mottoTranslation: "“By sea and by land”",
    },
    ancestry: {
      overline: "The Bloodline",
      title: "Are you one of us?",
      body: "Millions across the world are McDonalds — the many spellings of one clan: McDonald, MacDonald, McDonnell, MacDonell, McConnell and more. Find your name and take your place among them.",
      cta: "Trace Your Name",
      marquee: "CLANN DÒMHNAILL · BY SEA AND BY LAND · ",
    },
    join: {
      overline: "The Gathering",
      title: "Join the Clan.",
      body: "Receive the clan chronicle, gathering invitations and lineage research direct from the Highlands and the Glens.",
      name: "Your name",
      email: "Your email",
      message: "Tell us your family name or story (optional)",
      submit: "Pledge Your Name",
      submitting: "Sending…",
      success: "Thank you — your request has been received and is pending review by the clan gathering. You'll appear on the wall once confirmed.",
      error: "Something went wrong. Please try again.",
    },
    footer: {
      tagline: "The McDonalds — one clan of two shores, one sea.",
      rights: "This is a heritage tribute site. All rights reserved.",
      builtWith: "Woven with pride",
    },
  },

  fr: {
    nav: { history: "Origines", lineage: "Lignée", gallery: "Héritage", tartan: "Le Tartan", join: "Rejoindre le Clan", houses: "Maisons", kindred: "Parenté" },
    colors: { green: "Vert", blue: "Bleu", gold: "Or" },
    brandTagline: "Un Clan · Bien des Noms",
    hero: {
      overline: "Les McDonald · Clann Dòmhnaill · Depuis le XIIᵉ siècle",
      titleLines: ["Par la Mer", "et par la", "Terre"],
      subtitle:
        "L'histoire des McDonald — non pas un seul homme mais tout un peuple. Un clan aux mille branches dont le sang coule dans les vallées d'Écosse comme sur les côtes d'Irlande.",
      cta: "Découvrir notre histoire",
      secondary: "Retrouver votre lignée",
    },
    manifesto: {
      overline: "Les Origines",
      title: "Un lignage forgé dans le sel, la pierre et le feu.",
      chapters: [
        { num: "01", title: "Les Fils de Somerled", body: "De Somerled, le grand seigneur de guerre norvégo-gaélique qui brisa l'emprise des rois de la mer, descend notre lignée — un peuple ni tout à fait norrois ni tout à fait gaël, mais plus farouche que les deux." },
        { num: "02", title: "Seigneurs des Îles", body: "Pendant trois siècles, les MacDonald régnèrent en Seigneurs des Îles, un royaume maritime de drakkars et de lois qui ne répondait à aucune couronne que la sienne." },
        { num: "03", title: "Un Sang, Deux Rives", body: "Lorsque les galères traversèrent le canal du Nord, les MacDonnell prirent racine dans l'Antrim. Aujourd'hui encore, l'Irlande et l'Écosse partagent un cœur, un nom, un clan." },
      ],
    },
    timeline: {
      overline: "La Lignée",
      title: "Huit siècles, un fil ininterrompu.",
      events: [
        { year: "1156", title: "La Victoire de Somerled", body: "Somerled défait la flotte norroise au large d'Islay, fondant un royaume maritime gaélique." },
        { year: "1249", title: "Naissance du Clann Dòmhnaill", body: "Les descendants de Domhnall prennent son nom, et le clan proprement dit naît parmi les îles." },
        { year: "1411", title: "La Bataille de Harlaw", body: "Donald d'Islay marche sur Aberdeen — le clan au sommet de sa puissance militaire." },
        { year: "1493", title: "Chute de la Seigneurie", body: "La Seigneurie des Îles est confisquée par la couronne écossaise, dispersant le clan en branches fières." },
        { year: "1565", title: "La Branche d'Antrim", body: "Sorley Boy MacDonnell s'empare des vallées d'Antrim, liant à jamais l'Irlande au nom MacDonald." },
      ],
    },
    gallery: {
      overline: "L'Héritage",
      title: "Une terre qui se souvient.",
      subtitle: "Les vallées, ruines et rivages qui portent notre nom.",
      captions: ["Forteresses des Highlands", "La côte occidentale", "Ruines ancestrales", "L'île des ancêtres", "Le vieux pont"],
    },
    tartan: {
      overline: "Le Tartan",
      title: "Les fils de l'allégeance.",
      body: "Chaque couleur est un serment. Le rouge profond du courage, le vert des vallées, le sombre de la mer du Nord — tissés en un motif porté avec honneur de Skye à la côte d'Antrim.",
      motto: "Per mare per terras",
      mottoTranslation: "« Par la mer et par la terre »",
    },
    ancestry: {
      overline: "Le Sang",
      title: "Êtes-vous des nôtres ?",
      body: "Des millions de personnes à travers le monde sont des McDonald — les multiples graphies d'un même clan : McDonald, MacDonald, McDonnell, MacDonell, McConnell et bien d'autres. Retrouvez votre nom et prenez votre place parmi eux.",
      cta: "Retrouver votre nom",
      marquee: "CLANN DÒMHNAILL · PAR LA MER ET PAR LA TERRE · ",
    },
    join: {
      overline: "Le Rassemblement",
      title: "Rejoignez le Clan.",
      body: "Recevez la chronique du clan, les invitations aux rassemblements et les recherches de lignée, directement des Highlands et des vallées.",
      name: "Votre nom",
      email: "Votre e-mail",
      message: "Dites-nous votre nom de famille ou votre histoire (facultatif)",
      submit: "Engager votre nom",
      submitting: "Envoi…",
      success: "Merci — votre demande a bien été reçue et est en attente de validation par le rassemblement du clan. Vous apparaîtrez sur le mur une fois confirmée.",
      error: "Une erreur est survenue. Veuillez réessayer.",
    },
    footer: {
      tagline: "Les McDonald — un clan de deux rives, une seule mer.",
      rights: "Ceci est un site hommage au patrimoine. Tous droits réservés.",
      builtWith: "Tissé avec fierté",
    },
  },

  es: {
    nav: { history: "Orígenes", lineage: "Linaje", gallery: "Legado", tartan: "El Tartán", join: "Únete al Clan", houses: "Casas", kindred: "Parentela" },
    colors: { green: "Verde", blue: "Azul", gold: "Oro" },
    brandTagline: "Un Clan · Muchos Nombres",
    hero: {
      overline: "Los McDonald · Clann Dòmhnaill · Desde el siglo XII",
      titleLines: ["Por Mar", "y por", "Tierra"],
      subtitle:
        "La historia de los McDonald — no un solo hombre, sino todo un pueblo. Un clan de mil ramas cuya sangre corre por los valles de Escocia y las costas de Irlanda por igual.",
      cta: "Descubre nuestra historia",
      secondary: "Rastrea tu linaje",
    },
    manifesto: {
      overline: "Los Orígenes",
      title: "Un linaje forjado en sal, piedra y fuego.",
      chapters: [
        { num: "01", title: "Hijos de Somerled", body: "De Somerled, el gran señor de la guerra noruego-gaélico que rompió el dominio de los reyes del mar, desciende nuestro linaje — un pueblo ni del todo nórdico ni del todo gaélico, sino más feroz que ambos." },
        { num: "02", title: "Señores de las Islas", body: "Durante tres siglos los MacDonald reinaron como Señores de las Islas, un reino marítimo de drakkares y leyes que no respondía a más corona que la suya." },
        { num: "03", title: "Una Sangre, Dos Orillas", body: "Cuando las galeras cruzaron el Canal del Norte, los MacDonnell echaron raíces en Antrim. Hasta hoy, Irlanda y Escocia comparten un corazón, un nombre, un clan." },
      ],
    },
    timeline: {
      overline: "El Linaje",
      title: "Ocho siglos, un hilo ininterrumpido.",
      events: [
        { year: "1156", title: "La Victoria de Somerled", body: "Somerled derrota a la flota nórdica frente a Islay, fundando un reino marítimo gaélico." },
        { year: "1249", title: "Nace Clann Dòmhnaill", body: "Los descendientes de Domhnall toman su nombre, y el clan como tal nace entre las islas." },
        { year: "1411", title: "La Batalla de Harlaw", body: "Donald de Islay marcha sobre Aberdeen — el clan en la cúspide de su poder militar." },
        { year: "1493", title: "Caída del Señorío", body: "El Señorío de las Islas es confiscado por la corona escocesa, dispersando al clan en orgullosas ramas." },
        { year: "1565", title: "La Rama de Antrim", body: "Sorley Boy MacDonnell asegura los valles de Antrim, uniendo para siempre Irlanda al nombre MacDonald." },
      ],
    },
    gallery: {
      overline: "El Legado",
      title: "Tierra que recuerda.",
      subtitle: "Los valles, ruinas y costas que llevan nuestro nombre.",
      captions: ["Fortalezas de las Tierras Altas", "La costa occidental", "Ruinas ancestrales", "La isla de los ancestros", "El viejo puente"],
    },
    tartan: {
      overline: "El Tartán",
      title: "Hilos de lealtad.",
      body: "Cada color es un juramento. El rojo profundo del valor, el verde de los valles, la oscuridad del mar del norte — tejidos en un patrón vestido con honor desde Skye hasta la costa de Antrim.",
      motto: "Per mare per terras",
      mottoTranslation: "«Por mar y por tierra»",
    },
    ancestry: {
      overline: "La Sangre",
      title: "¿Eres uno de los nuestros?",
      body: "Millones en todo el mundo son McDonald — las muchas grafías de un mismo clan: McDonald, MacDonald, McDonnell, MacDonell, McConnell y más. Encuentra tu nombre y ocupa tu lugar entre ellos.",
      cta: "Rastrea tu nombre",
      marquee: "CLANN DÒMHNAILL · POR MAR Y POR TIERRA · ",
    },
    join: {
      overline: "La Reunión",
      title: "Únete al Clan.",
      body: "Recibe la crónica del clan, invitaciones a reuniones e investigación de linaje directamente desde las Tierras Altas y los valles.",
      name: "Tu nombre",
      email: "Tu correo electrónico",
      message: "Cuéntanos tu apellido o tu historia (opcional)",
      submit: "Comprometer tu nombre",
      submitting: "Enviando…",
      success: "Gracias — tu solicitud se ha recibido y está pendiente de revisión por la reunión del clan. Aparecerás en el muro una vez confirmada.",
      error: "Algo salió mal. Inténtalo de nuevo.",
    },
    footer: {
      tagline: "Los McDonald — un clan de dos orillas, un solo mar.",
      rights: "Este es un sitio de homenaje al patrimonio. Todos los derechos reservados.",
      builtWith: "Tejido con orgullo",
    },
  },

  de: {
    nav: { history: "Ursprünge", lineage: "Abstammung", gallery: "Erbe", tartan: "Das Tartan", join: "Dem Clan beitreten", houses: "Häuser", kindred: "Sippe" },
    colors: { green: "Grün", blue: "Blau", gold: "Gold" },
    brandTagline: "Ein Clan · Viele Namen",
    hero: {
      overline: "Die McDonalds · Clann Dòmhnaill · Seit dem 12. Jahrhundert",
      titleLines: ["Über See", "und über", "Land"],
      subtitle:
        "Die Geschichte der McDonalds — nicht ein einzelner Mann, sondern ein ganzes Volk. Ein vielverzweigter Clan, dessen Blut durch die Täler Schottlands und die Küsten Irlands gleichermaßen fließt.",
      cta: "Unsere Geschichte entdecken",
      secondary: "Deine Abstammung verfolgen",
    },
    manifesto: {
      overline: "Die Ursprünge",
      title: "Eine Sippe, geschmiedet aus Salz, Stein und Feuer.",
      chapters: [
        { num: "01", title: "Söhne Somerleds", body: "Von Somerled, dem großen norwegisch-gälischen Kriegsherrn, der die Macht der Seekönige brach, stammt unsere Linie ab — ein Volk, weder ganz nordisch noch ganz gälisch, sondern wilder als beide." },
        { num: "02", title: "Herren der Inseln", body: "Drei Jahrhunderte lang herrschten die MacDonalds als Herren der Inseln, ein Seereich aus Langschiffen und Gesetz, das keiner Krone außer der eigenen antwortete." },
        { num: "03", title: "Ein Blut, zwei Küsten", body: "Als die Galeeren den Nordkanal überquerten, fassten die MacDonnells in Antrim Fuß. Bis heute teilen Irland und Schottland ein Herz, einen Namen, einen Clan." },
      ],
    },
    timeline: {
      overline: "Die Abstammung",
      title: "Acht Jahrhunderte, ein ungebrochener Faden.",
      events: [
        { year: "1156", title: "Somerleds Sieg", body: "Somerled besiegt die nordische Flotte vor Islay und legt den Grundstein eines gälischen Seereichs." },
        { year: "1249", title: "Clann Dòmhnaill benannt", body: "Die Nachkommen von Domhnall nehmen seinen Namen an, und der eigentliche Clan wird auf den Inseln geboren." },
        { year: "1411", title: "Die Schlacht von Harlaw", body: "Donald von Islay marschiert auf Aberdeen — der Clan auf dem Höhepunkt seiner militärischen Macht." },
        { year: "1493", title: "Fall der Lordschaft", body: "Die Lordschaft der Inseln wird an die schottische Krone verwirkt und zerstreut den Clan in stolze Zweige." },
        { year: "1565", title: "Der Zweig von Antrim", body: "Sorley Boy MacDonnell sichert die Täler von Antrim und bindet Irland für immer an den Namen MacDonald." },
      ],
    },
    gallery: {
      overline: "Das Erbe",
      title: "Land, das sich erinnert.",
      subtitle: "Die Täler, Ruinen und Küsten, die unseren Namen tragen.",
      captions: ["Highland-Festungen", "Die Westküste", "Ahnenruinen", "Insel der Vorfahren", "Die alte Brücke"],
    },
    tartan: {
      overline: "Das Tartan",
      title: "Fäden der Treue.",
      body: "Jede Farbe ist ein Schwur. Das tiefe Rot des Mutes, das Grün der Täler, das Dunkel der Nordsee — gewebt zu einem Muster, das von Skye bis zur Küste von Antrim mit Ehre getragen wird.",
      motto: "Per mare per terras",
      mottoTranslation: "„Über See und über Land“",
    },
    ancestry: {
      overline: "Die Blutlinie",
      title: "Gehörst du zu uns?",
      body: "Millionen weltweit sind McDonalds — die vielen Schreibweisen eines Clans: McDonald, MacDonald, McDonnell, MacDonell, McConnell und mehr. Finde deinen Namen und nimm deinen Platz unter ihnen ein.",
      cta: "Deinen Namen verfolgen",
      marquee: "CLANN DÒMHNAILL · ÜBER SEE UND ÜBER LAND · ",
    },
    join: {
      overline: "Die Versammlung",
      title: "Tritt dem Clan bei.",
      body: "Erhalte die Clan-Chronik, Einladungen zu Versammlungen und Abstammungsforschung direkt aus den Highlands und den Tälern.",
      name: "Dein Name",
      email: "Deine E-Mail",
      message: "Nenne uns deinen Familiennamen oder deine Geschichte (optional)",
      submit: "Deinen Namen verpflichten",
      submitting: "Senden…",
      success: "Danke — deine Anfrage ist eingegangen und wird von der Clan-Versammlung geprüft. Nach der Bestätigung erscheinst du an der Wand.",
      error: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
    },
    footer: {
      tagline: "Die McDonalds — ein Clan zweier Küsten, ein Meer.",
      rights: "Dies ist eine Erbe-Hommage-Website. Alle Rechte vorbehalten.",
      builtWith: "Mit Stolz gewoben",
    },
  },

  it: {
    nav: { history: "Origini", lineage: "Lignaggio", gallery: "Eredità", tartan: "Il Tartan", join: "Unisciti al Clan", houses: "Casate", kindred: "Parentela" },
    colors: { green: "Verde", blue: "Blu", gold: "Oro" },
    brandTagline: "Un Clan · Molti Nomi",
    hero: {
      overline: "I McDonald · Clann Dòmhnaill · Dal XII secolo",
      titleLines: ["Per Mare", "e per", "Terra"],
      subtitle:
        "La storia dei McDonald — non un solo uomo, ma un intero popolo. Un clan dai mille rami il cui sangue scorre nelle valli di Scozia e sulle coste d'Irlanda.",
      cta: "Scopri la nostra storia",
      secondary: "Ricostruisci il tuo lignaggio",
    },
    manifesto: {
      overline: "Le Origini",
      title: "Un lignaggio forgiato nel sale, nella pietra e nel fuoco.",
      chapters: [
        { num: "01", title: "Figli di Somerled", body: "Da Somerled, il grande signore della guerra norreno-gaelico che spezzò il dominio dei re del mare, discende il nostro lignaggio — un popolo né del tutto norreno né del tutto gaelico, ma più fiero di entrambi." },
        { num: "02", title: "Signori delle Isole", body: "Per tre secoli i MacDonald regnarono come Signori delle Isole, un regno marittimo di navi lunghe e di leggi che non rispondeva ad altra corona che la propria." },
        { num: "03", title: "Un Sangue, Due Sponde", body: "Quando le galee attraversarono il Canale del Nord, i MacDonnell misero radici nell'Antrim. Ancora oggi Irlanda e Scozia condividono un cuore, un nome, un clan." },
      ],
    },
    timeline: {
      overline: "Il Lignaggio",
      title: "Otto secoli, un filo ininterrotto.",
      events: [
        { year: "1156", title: "La Vittoria di Somerled", body: "Somerled sconfigge la flotta norrena al largo di Islay, fondando un regno marittimo gaelico." },
        { year: "1249", title: "Nasce Clann Dòmhnaill", body: "I discendenti di Domhnall ne assumono il nome e il clan vero e proprio nasce tra le isole." },
        { year: "1411", title: "La Battaglia di Harlaw", body: "Donald di Islay marcia su Aberdeen — il clan all'apice della sua potenza militare." },
        { year: "1493", title: "Caduta della Signoria", body: "La Signoria delle Isole è confiscata dalla corona scozzese, disperdendo il clan in fieri rami." },
        { year: "1565", title: "Il Ramo di Antrim", body: "Sorley Boy MacDonnell assicura le valli di Antrim, legando per sempre l'Irlanda al nome MacDonald." },
      ],
    },
    gallery: {
      overline: "L'Eredità",
      title: "Terra che ricorda.",
      subtitle: "Le valli, le rovine e le coste che portano il nostro nome.",
      captions: ["Roccaforti delle Highlands", "La costa occidentale", "Rovine ancestrali", "L'isola degli antenati", "Il vecchio ponte"],
    },
    tartan: {
      overline: "Il Tartan",
      title: "Fili di lealtà.",
      body: "Ogni colore è un giuramento. Il rosso profondo del coraggio, il verde delle valli, il buio del mare del nord — intessuti in un motivo indossato con onore da Skye alla costa di Antrim.",
      motto: "Per mare per terras",
      mottoTranslation: "«Per mare e per terra»",
    },
    ancestry: {
      overline: "Il Sangue",
      title: "Sei uno di noi?",
      body: "Milioni nel mondo sono McDonald — le molte grafie di un solo clan: McDonald, MacDonald, McDonnell, MacDonell, McConnell e altri. Trova il tuo nome e prendi il tuo posto tra loro.",
      cta: "Ricostruisci il tuo nome",
      marquee: "CLANN DÒMHNAILL · PER MARE E PER TERRA · ",
    },
    join: {
      overline: "Il Raduno",
      title: "Unisciti al Clan.",
      body: "Ricevi la cronaca del clan, gli inviti ai raduni e le ricerche sul lignaggio direttamente dalle Highlands e dalle valli.",
      name: "Il tuo nome",
      email: "La tua email",
      message: "Raccontaci il tuo cognome o la tua storia (facoltativo)",
      submit: "Impegna il tuo nome",
      submitting: "Invio…",
      success: "Grazie — la tua richiesta è stata ricevuta ed è in attesa di revisione dal raduno del clan. Apparirai sul muro una volta confermata.",
      error: "Qualcosa è andato storto. Riprova.",
    },
    footer: {
      tagline: "I McDonald — un clan di due sponde, un solo mare.",
      rights: "Questo è un sito omaggio al patrimonio. Tutti i diritti riservati.",
      builtWith: "Tessuto con orgoglio",
    },
  },

  gd: {
    nav: { history: "Tùsan", lineage: "Sinnsireachd", gallery: "Dìleab", tartan: "An Tartan", join: "Thig dhan Chinneadh", houses: "Taighean", kindred: "An Cinneadh" },
    colors: { green: "Uaine", blue: "Gorm", gold: "Òr" },
    brandTagline: "Aon Chinneadh · Iomadh Ainm",
    hero: {
      overline: "Na Dòmhnallaich · Clann Dòmhnaill · Bhon 12mh linn",
      titleLines: ["Air Muir", "agus air", "Tìr"],
      subtitle:
        "Sgeul nan Dòmhnallach — chan e aon duine ach sluagh gu lèir. Cinneadh le iomadh meur aig a bheil fuil a' ruith tro ghlinn na h-Alba agus costa na h-Èireann.",
      cta: "Faigh a-mach ar sgeul",
      secondary: "Lorg do shinnsireachd",
    },
    manifesto: {
      overline: "Na Tùsan",
      title: "Cinneadh air a dhealbh à salann, clach is teine.",
      chapters: [
        { num: "01", title: "Mic Shomhairle", body: "Bho Shomhairle, an ceann-cinnidh mòr Lochlannach-Gàidhealach a bhris grèim rìghrean na mara, tha ar sliochd a' teàrnadh — sluagh nach eil gu tur Lochlannach no gu tur Gàidhealach, ach nas fiadhaiche na an dà chuid." },
        { num: "02", title: "Triathan nan Eilean", body: "Airson trì linntean bha na Dòmhnallaich nan Triathan air na h-Eileanan, rìoghachd mara de bhirlinnean is lagh nach do fhreagair crùn sam bith ach an crùn fhèin." },
        { num: "03", title: "Aon Fhuil, Dà Chladach", body: "Nuair a thar-chuir na birlinnean an Caol a Tuath, ghabh na Dòmhnallaich freumh ann an Aontroim. Chun an latha an-diugh tha Èirinn is Alba a' roinn aon chridhe, aon ainm, aon chinneadh." },
      ],
    },
    timeline: {
      overline: "An t-Sinnsireachd",
      title: "Ochd linntean, aon snàithlean gun bhriseadh.",
      events: [
        { year: "1156", title: "Buaidh Shomhairle", body: "Rinn Somhairle a' chùis air cabhlach nan Lochlannach far Ìle, a' stèidheachadh rìoghachd-mara Gàidhealach." },
        { year: "1249", title: "Clann Dòmhnaill air ainmeachadh", body: "Ghabh sliochd Dhòmhnaill ainm, agus rugadh an cinneadh ceart am measg nan eilean." },
        { year: "1411", title: "Blàr Har Law", body: "Choisich Dòmhnall Ìle air Obar Dheathain — an cinneadh aig àirde a chumhachd cogaidh." },
        { year: "1493", title: "Tuiteam nan Triath", body: "Chaidh Triathachd nan Eilean a thoirt don chrùn Albannach, a' sgapadh a' chinnidh na mheuran uaibhreach." },
        { year: "1565", title: "Meur Aontroim", body: "Ghlèidh Somhairle Buidhe Mac Dhòmhnaill glinn Aontroim, a' ceangal na h-Èireann gu bràth ri ainm Dhòmhnaill." },
      ],
    },
    gallery: {
      overline: "An Dìleab",
      title: "Fearann a chuimhnicheas.",
      subtitle: "Na glinn, na tobhtaichean is na cladaichean a ghiùlaineas ar n-ainm.",
      captions: ["Daingnich na Gàidhealtachd", "An costa an iar", "Tobhtaichean nan sinnsear", "Eilean nan sinnsear", "An seann drochaid"],
    },
    tartan: {
      overline: "An Tartan",
      title: "Snàithlean dìlseachd.",
      body: "Tha gach dath na bhòid. Dearg domhainn a' mhisneachd, uaine nan gleann, dorchadas na mara a tuath — air am fighe na phàtran a chaitear le urram bho Sgìthe gu costa Aontroim.",
      motto: "Per mare per terras",
      mottoTranslation: "“Air muir agus air tìr”",
    },
    ancestry: {
      overline: "An Fhuil",
      title: "A bheil thu nar measg?",
      body: "Tha milleanan air feadh an t-saoghail nan Dòmhnallaich — na h-iomadh litreachadh air aon chinneadh: McDonald, MacDonald, McDonnell, MacDonell, McConnell agus tuilleadh. Lorg d' ainm agus gabh d' àite nam measg.",
      cta: "Lorg d' ainm",
      marquee: "CLANN DÒMHNAILL · AIR MUIR AGUS AIR TÌR · ",
    },
    join: {
      overline: "An Cruinneachadh",
      title: "Thig dhan Chinneadh.",
      body: "Faigh eachdraidh a' chinnidh, cuiridhean gu cruinneachaidhean agus rannsachadh sinnsireachd bho Ghàidhealtachd is na glinn.",
      name: "D' ainm",
      email: "Do phost-d",
      message: "Innis dhuinn d' ainm-teaghlaich no do sgeul (roghainneil)",
      submit: "Geall d' ainm",
      submitting: "A' cur…",
      success: "Tapadh leat — chaidh d' iarrtas fhaighinn agus tha e a' feitheamh ri sgrùdadh le cruinneachadh a' chinnidh. Nochdaidh tu air a' bhalla nuair a thèid a dhearbhadh.",
      error: "Chaidh rudeigin ceàrr. Feuch a-rithist.",
    },
    footer: {
      tagline: "Na Dòmhnallaich — aon chinneadh de dhà chladach, aon mhuir.",
      rights: "'S e làrach-urraim dualchais a tha seo. Gach còir glèidhte.",
      builtWith: "Air fhighe le pròis",
    },
  },
};
